/* Live agent-upgrade button — pushes the canonical agent.py from the
 * backend onto every VM of a cloud and restarts the hoston-agent service.
 *
 * Used to roll out hotfixes (e.g. new NC warning patches in agent.py) to
 * existing clouds without re-provisioning. Calls POST /api/admin/clouds/{id}/agent/upgrade.
 */
import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Wrench, CheckCircle2, AlertTriangle } from "lucide-react";

export default function CloudAgentUpgradePanel({ cloudId }) {
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [err, setErr] = useState(null);

    const upgrade = async () => {
        if (!window.confirm(
            "Push the current agent.py to EVERY VM of this cloud and restart " +
            "the hoston-agent? Safe & idempotent (enrollment is preserved) — " +
            "each VM is briefly unreachable for CM during the restart."
        )) return;
        setBusy(true);
        setErr(null);
        setResult(null);
        try {
            const { data } = await api.post(`/api/admin/clouds/${cloudId}/agent/upgrade`);
            setResult(data);
        } catch (e) {
            setErr(formatApiError(e));
        }
        setBusy(false);
    };

    return (
        <div data-testid="cloud-agent-upgrade-panel" className="cm-card p-5">
            <div className="cm-h3 mb-3 flex items-center gap-2">
                <Wrench size={14} className="text-zinc-400" />
                Update agent
            </div>
            <p className="text-xs text-zinc-400 mb-3">
                Pushes the canonical <code className="font-mono text-zinc-300">agent.py</code>{" "}
                onto <strong>all</strong> VMs of this cloud (worker, web, Redis and
                NAS), restarts the Hoston agent and renews the enrollment token if
                needed. Idempotent — the agent then updates itself. The table below
                shows, per VM, whether the update succeeded.
            </p>
            <button
                data-testid="cloud-agent-upgrade-button"
                className="cm-btn-secondary text-xs flex items-center gap-1.5"
                disabled={busy}
                onClick={upgrade}
            >
                <Wrench size={11} /> {busy ? "Pushing…" : "Push agent to all VMs"}
            </button>

            {result && (
                <div
                    data-testid="cloud-agent-upgrade-result"
                    className="mt-3 bg-zinc-950 border border-zinc-800 p-2 text-xs font-mono"
                >
                    <div className="flex justify-between mb-2">
                        <span className="text-zinc-300">
                            {result.vms_ok} / {result.vms_total} VMs updated
                        </span>
                        {result.vms_failed === 0 ? (
                            <span className="text-emerald-500 flex items-center gap-1">
                                <CheckCircle2 size={11} /> all OK
                            </span>
                        ) : (
                            <span className="text-amber-500 flex items-center gap-1">
                                <AlertTriangle size={11} /> {result.vms_failed} failed
                            </span>
                        )}
                    </div>
                    <div className="text-[11px] text-zinc-500 mb-1">
                        cm_base: {result.cm_base}
                    </div>
                    <div className="space-y-1">
                        {(result.per_vm || []).map((v) => (
                            <div
                                key={v.instance_id}
                                className="bg-zinc-900 p-1.5 border border-zinc-800 break-words"
                                data-testid={`cloud-agent-upgrade-result-vm-${v.instance_id}`}
                            >
                                <div className="flex justify-between">
                                    <span>
                                        {v.role}#{v.vmid} @ {v.node}
                                    </span>
                                    <span className={v.ok ? "text-emerald-500" : "text-rose-400"}>
                                        rc={v.exit_code} · {v.duration_ms}ms
                                    </span>
                                </div>
                                {!v.ok && v.stderr_tail && (
                                    <div className="text-[11px] text-rose-300 mt-0.5 break-words">
                                        {v.stderr_tail}
                                    </div>
                                )}
                                {v.ok && v.stdout_tail && (
                                    <div className="text-[11px] text-zinc-500 mt-0.5 break-words">
                                        {v.stdout_tail}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {err && (
                <div
                    data-testid="cloud-agent-upgrade-error"
                    className="mt-3 bg-rose-950/30 border border-rose-800 text-rose-300 text-xs p-2 font-mono"
                >
                    {err}
                </div>
            )}
        </div>
    );
}
